// ...existing code...
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const formFields = [
  { name: "productName", label: "Product Name", type: "text", required: true },
  { name: "productCode", label: "Product Code", type: "text", required: true },
  { name: "bin", label: "BIN", type: "text", required: true },
];

const AddCardProductModal = ({ open, handleClose, onSubmit, isSubmitting = false }) => {
  const [formData, setFormData] = useState(() => {
    const initial = {};
    formFields.forEach((f) => (initial[f.name] = ""));
    return initial;
  });

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      // reset form whenever modal opens
      const initial = {};
      formFields.forEach((f) => (initial[f.name] = ""));
      setFormData(initial);
      setConfirmOpen(false);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid = Object.values(formData).every((v) => String(v).trim() !== "");

  const submit = (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;
    // Open confirmation modal instead of directly calling API
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    // call parent onSubmit; parent manages isSubmitting and closing add modal on success
    if (onSubmit) {
      await onSubmit(formData);
    }
    setConfirmOpen(false);
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  // style that forces the outlined field border and label color to rgba(0,0,0,0.87) when focused/typing
  const textFieldSx = {
    // border color when focused
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
    // border color on hover
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: "rgba(0, 0, 0, 0.87)",
    },
    // label color when focused
    "& .MuiInputLabel-root.Mui-focused": {
      color: "rgba(0, 0, 0, 0.87)",
    },
    // label color when shrunk (has value)
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      color: "rgba(0, 0, 0, 0.87)",
    },
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Add New Card Product
          </Typography>

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {formFields.map((field) => (
              <TextField
                key={field.name}
                fullWidth
                label={field.label}
                name={field.name}
                type={field.type}
                value={formData[field.name]}
                onChange={handleChange}
                required={field.required}
                disabled={isSubmitting}
                sx={textFieldSx}
              />
            ))}

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid || isSubmitting}
              sx={{
                mt: 2,
                backgroundColor: isFormValid && !isSubmitting ? "#ED141F" : "rgba(237,20,31,0.5)",
                "&:hover": {
                  backgroundColor: isFormValid && !isSubmitting ? "#C21014" : "rgba(237,20,31,0.5)",
                },
              }}
            >
              {isSubmitting ? (
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={20} color="inherit" />
                  Submitting...
                </span>
              ) : (
                "Add Card Product"
              )}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={handleCancelConfirm} aria-labelledby="confirm-dialog-title">
        <DialogTitle id="confirm-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to create this card product?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelConfirm}
            disabled={isSubmitting}
            sx={{
              color: "#ED141F",
              "&:hover": { backgroundColor: "rgba(237,20,31,0.08)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#ED141F",
              "&:hover": { backgroundColor: "#C21014" },
            }}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddCardProductModal;
// ...existing code...
